import type { SubmitEventHandler } from 'react';

import { use, useCallback, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { z } from 'zod';

import { getTestProps } from '../../../../lib/helpers';

import Button from '../../../../components/Button/Button';

import { ModalInstanceContext } from '../../../../contexts/ModalContext';

import { WarningTriangleIcon } from '../../../../images/shapes';

import { useAppForm } from '../../../../form-hooks/form';

interface ManageDataModalContentProps {
  expectedSpaceId?: string;
  testId?: string;
}

const ManageDataModalContent = ({
  expectedSpaceId,
  testId,
}: ManageDataModalContentProps) => {
  const { t } = useTranslation();
  const modalInstance = use(ModalInstanceContext);

  const validation = useMemo(
    () =>
      z.object({
        spaceId: z
          .string()
          .refine((value) => value.trim() === expectedSpaceId, {
            message: t('SpaceSettings.ManageData.Modal.IdMismatch'),
          }),
      }),
    [expectedSpaceId, t],
  );

  const form = useAppForm({
    defaultValues: { spaceId: '' },
    validators: { onSubmit: validation },
    onSubmit: ({ value }) => modalInstance.resolve?.(value.spaceId.trim()),
  });

  const handleSubmit = useCallback<SubmitEventHandler>(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      form.handleSubmit();
    },
    [form],
  );

  return (
    <div
      className="text-slate-900 dark:text-white"
      {...getTestProps(testId, 'clear-data-modal-container')}
    >
      <form.AppForm>
        <form
          id="clear-data-modal-form"
          className="w-full flex flex-col gap-4"
          onSubmit={handleSubmit}
          noValidate
          {...getTestProps(testId)}
        >
          <h3>
            <Trans i18nKey="SpaceSettings.ManageData.Modal.Title" />
          </h3>
          <p>
            <Trans i18nKey="SpaceSettings.ManageData.Modal.Info" />
          </p>

          <div
            className={twMerge(
              'flex items-center gap-4 w-full h-12 py-3 px-4 mb-4',
              'rounded-lg border border-red bg-red-50',
              'dark:bg-red-950 ',
            )}
          >
            <WarningTriangleIcon className={'w-5 text-red-600 dark:text-red'} />
            <Trans i18nKey="SpaceSettings.ManageData.Modal.Warning" />
          </div>

          <form.AppField name="spaceId">
            {(field) => (
              <field.Input
                label={t('SpaceSettings.ManageData.Modal.InputLabel')}
                additionalClasses="text-left"
                required
                autoFocus
                autoComplete="off"
                spellCheck={false}
                {...getTestProps(testId, 'space-id', 'testId')}
              />
            )}
          </form.AppField>

          <div
            className={twMerge(
              'flex justify-center gap-3 absolute bottom-3 pt-3',
              'border-t border-slate-100 dark:border-slate-800',
              'left-1/2 -translate-x-1/2 w-full',
            )}
          >
            <Button
              type="button"
              onClick={() => modalInstance.resolve?.(false)}
              buttonColor="blueBordered"
              {...getTestProps(testId, 'cancel', 'testId')}
            >
              {t('Global.Cancel')}
            </Button>

            <Button
              type="submit"
              buttonColor="blue"
              {...getTestProps(testId, 'clear-data', 'testId')}
            >
              {t('SpaceSettings.ManageData.Modal.Confirm')}
            </Button>
          </div>
        </form>
      </form.AppForm>
    </div>
  );
};

export default ManageDataModalContent;
